import { useLocale } from "@jmc/core/src/hooks/useLocale/index";
import { changeLocalization, type ModuleType } from "@jmc/utils/utils/format-date";
import { type ReactNode, useEffect, useRef, useState } from "react";
import yn from "yn";

interface PropType {
    children: ReactNode;
}

export const DayjsWrapper = ({ children }: PropType): JSX.Element => {
    const locale = useLocale();
    // because we want the children of this component to be server-side rendered
    // we need to set the loaded state to true initially
    const [loaded, setLoaded] = useState(true);
    const isInitialMount = useRef(true);
    const currentLocale = useRef<string | null>(null);

    // await async DayJS configuration before rerendering everything
    useEffect(() => {
        // Skip the initial effect run to prevent browser re-render on mount
        // This prevents the component from flashing (rendering children, then null, then children again)
        // while still allowing proper locale changes on subsequent updates
        if (isInitialMount.current) {
            isInitialMount.current = false;
            currentLocale.current = locale;
            return; // Skip the first effect run
        }

        // Only change locale if it's actually different from the current one
        if (currentLocale.current === locale) {
            return; // No locale change needed
        }

        currentLocale.current = locale;
        setLoaded(false);
        async function changeLocale(loc: string): Promise<ModuleType> {
            return await changeLocalization(loc);
        }
        changeLocale(locale).then(() => {
            setLoaded(true);
        });
    }, [locale]);

    return !loaded && !yn(process.env.EVERYTHING_PUBLIC, { default: false }) ? null : <>{children}</>;
};
